/**
 * Fabric specs panel — the garment satellite on the generic item (GSM,
 * composition, width, colour, shrinkage). Read view + inline editor over
 * `itemSpecs.get` / `itemSpecs.upsert` (behind `garment.materials`). Rendered
 * only inside the garment-gated "Fabric specs" tab on the item detail page.
 *
 * Shrinkage is stored as basis points (`shrinkageBp`) but shown/entered as a
 * percent — 3% ⇄ 300 bp — so the operator never types four zeros.
 *
 * @see packages/domain/src/garment/item-specs
 */
import { useState } from "react";
import { Palette, Pencil, X } from "lucide-react";
import { Button, Input, toast } from "@flint/ui";
import { trpc } from "../../../lib/trpc.ts";

interface FabricSpecsPanelProps {
  itemId: string;
}

export function FabricSpecsPanel({ itemId }: FabricSpecsPanelProps) {
  const utils = trpc.useUtils();
  const query = trpc.itemSpecs.get.useQuery({ itemId });
  const [editing, setEditing] = useState(false);

  const upsert = trpc.itemSpecs.upsert.useMutation({
    onSuccess: async () => {
      await utils.itemSpecs.get.invalidate({ itemId });
      setEditing(false);
      toast.success("Fabric specs saved");
    },
    onError: (e: { message: string }) => toast.error("Couldn't save", { description: e.message }),
  });

  if (query.isLoading) return <p className="text-sm text-muted">Loading fabric specs…</p>;
  if (query.error) return <p className="text-bad">Couldn't load specs: {query.error.message}</p>;

  const spec = query.data ?? null;

  if (editing) {
    return <FabricSpecsForm itemId={itemId} spec={spec} pending={upsert.isPending} onCancel={() => setEditing(false)} onSave={(v) => upsert.mutate(v)} />;
  }

  const hasAny =
    spec !== null &&
    (spec.gsm !== null ||
      spec.composition !== null ||
      spec.widthCm !== null ||
      spec.colour !== null ||
      spec.shrinkageBp !== null);

  return (
    <div className="panel">
      <div className="panel-head">
        <h3>Fabric specs</h3>
        <button type="button" className="tag-add" onClick={() => setEditing(true)}>
          <Pencil size={10} aria-hidden /> {hasAny ? "Edit" : "Add"}
        </button>
      </div>
      <div className="panel-body">
        {hasAny ? (
          <dl className="specs">
            <SpecRow label="GSM" value={spec.gsm !== null ? `${spec.gsm} g/m²` : "—"} mono />
            <SpecRow label="Composition" value={spec.composition ?? "—"} />
            <SpecRow label="Width" value={spec.widthCm !== null ? `${spec.widthCm} cm` : "—"} mono />
            <SpecColourRow colour={spec.colour} colourHex={spec.colourHex} />
            <SpecRow label="Shrinkage" value={spec.shrinkageBp !== null ? `${spec.shrinkageBp / 100}%` : "—"} mono />
          </dl>
        ) : (
          <div className="flex flex-col items-start gap-2">
            <p className="desc" style={{ color: "var(--muted)", fontStyle: "italic" }}>
              No fabric specs yet. Record GSM, composition, width, colour and shrinkage so cutting and QC
              work off the same cloth details.
            </p>
            <Button size="sm" onClick={() => setEditing(true)}>
              <Palette size={13} aria-hidden /> Add fabric specs
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

// ── editor ─────────────────────────────────────────────────────

interface UpsertInput {
  itemId: string;
  gsm: number | null;
  composition: string | null;
  widthCm: number | null;
  colour: string | null;
  colourHex: string | null;
  shrinkageBp: number | null;
}

interface FabricSpecsFormProps {
  itemId: string;
  spec: {
    gsm: number | null;
    composition: string | null;
    widthCm: number | null;
    colour: string | null;
    colourHex: string | null;
    shrinkageBp: number | null;
  } | null;
  pending: boolean;
  onCancel: () => void;
  onSave: (input: UpsertInput) => void;
}

function FabricSpecsForm({ itemId, spec, pending, onCancel, onSave }: FabricSpecsFormProps) {
  const [gsm, setGsm] = useState(spec?.gsm !== null && spec?.gsm !== undefined ? String(spec.gsm) : "");
  const [composition, setComposition] = useState(spec?.composition ?? "");
  const [widthCm, setWidthCm] = useState(spec?.widthCm !== null && spec?.widthCm !== undefined ? String(spec.widthCm) : "");
  const [colour, setColour] = useState(spec?.colour ?? "");
  const [colourHex, setColourHex] = useState(spec?.colourHex ?? "");
  const [shrinkagePct, setShrinkagePct] = useState(
    spec?.shrinkageBp !== null && spec?.shrinkageBp !== undefined ? String(spec.shrinkageBp / 100) : "",
  );

  const previewHex = /^#?[0-9a-fA-F]{3,8}$/.test(colourHex.trim()) ? normalizeHex(colourHex.trim()) : null;

  function submit() {
    const gsmN = parseIntOrNull(gsm);
    if (gsmN !== null && (gsmN < 0 || gsmN > 2000)) {
      toast.error("GSM must be 0–2000");
      return;
    }
    const widthN = parseIntOrNull(widthCm);
    if (widthN !== null && (widthN < 0 || widthN > 1000)) {
      toast.error("Width must be 0–1000 cm");
      return;
    }
    const shrinkBp = parsePercentToBp(shrinkagePct);
    if (shrinkBp !== null && (shrinkBp < 0 || shrinkBp > 10000)) {
      toast.error("Shrinkage must be 0–100%");
      return;
    }

    onSave({
      itemId,
      gsm: gsmN,
      composition: composition.trim() || null,
      widthCm: widthN,
      colour: colour.trim() || null,
      colourHex: previewHex,
      shrinkageBp: shrinkBp,
    });
  }

  return (
    <div className="panel">
      <div className="panel-head">
        <h3>Fabric specs</h3>
        <button type="button" className="tag-add" onClick={onCancel}>
          <X size={10} aria-hidden /> Cancel
        </button>
      </div>
      <div className="panel-body">
        <div className="flex flex-wrap items-end gap-3">
          <Field label="GSM (g/m²)">
            <Input value={gsm} onChange={(e) => setGsm(e.currentTarget.value)} placeholder="180" inputMode="numeric" className="h-8 w-24 text-xs" />
          </Field>
          <Field label="Composition">
            <Input value={composition} onChange={(e) => setComposition(e.currentTarget.value)} placeholder="100% Cotton" className="h-8 w-48 text-xs" />
          </Field>
          <Field label="Width (cm)">
            <Input value={widthCm} onChange={(e) => setWidthCm(e.currentTarget.value)} placeholder="180" inputMode="numeric" className="h-8 w-24 text-xs" />
          </Field>
          <Field label="Colour">
            <Input value={colour} onChange={(e) => setColour(e.currentTarget.value)} placeholder="Navy" className="h-8 w-36 text-xs" />
          </Field>
          <Field label="Colour hex">
            <div className="flex items-center gap-1.5">
              <Input value={colourHex} onChange={(e) => setColourHex(e.currentTarget.value)} placeholder="#1b2a4a" className="h-8 w-28 font-mono text-xs" />
              <span
                aria-hidden
                className="h-6 w-6 flex-shrink-0 rounded-sm border border-line"
                style={{ background: previewHex ?? "transparent" }}
              />
            </div>
          </Field>
          <Field label="Shrinkage (%)">
            <Input value={shrinkagePct} onChange={(e) => setShrinkagePct(e.currentTarget.value)} placeholder="3" inputMode="decimal" className="h-8 w-24 text-xs" />
          </Field>
          <Button size="sm" disabled={pending} onClick={submit}>
            {pending ? "Saving…" : "Save specs"}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ── helpers ────────────────────────────────────────────────────

function SpecRow({ label, value, mono = false }: { label: string; value: string; mono?: boolean }) {
  return (
    <div>
      <dt>{label}</dt>
      <dd>{mono ? <span className="mono">{value}</span> : value}</dd>
    </div>
  );
}

function SpecColourRow({ colour, colourHex }: { colour: string | null; colourHex: string | null }) {
  return (
    <div>
      <dt>Colour</dt>
      <dd>
        <span className="inline-flex items-center gap-1.5">
          {colourHex ? (
            <span aria-hidden className="inline-block h-3 w-3 rounded-sm border border-line" style={{ background: colourHex }} />
          ) : null}
          {colour ?? "—"}
        </span>
      </dd>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-xs font-medium uppercase tracking-wider text-muted">{label}</span>
      {children}
    </label>
  );
}

function parseIntOrNull(raw: string): number | null {
  const t = raw.trim();
  if (t === "") return null;
  const n = Math.round(Number(t));
  return Number.isFinite(n) ? n : null;
}

function parsePercentToBp(raw: string): number | null {
  const t = raw.trim();
  if (t === "") return null;
  const n = Number(t);
  return Number.isFinite(n) ? Math.round(n * 100) : null;
}

function normalizeHex(raw: string): string {
  return raw.startsWith("#") ? raw : `#${raw}`;
}
